// @flow
import BuilderSideMenuSettings from 'pages/Components/BuilderSideMenuSettings'
import { FormBuilderContext } from 'pages/Contexts/FormBuilderContext'
import BuilderSideMenuAdvancedSettings from 'pages/FormBuilder/Components/BuilderSideMenuAdvancedSettings'
import BuilderSideMenuEditForm from 'pages/FormBuilder/Components/BuilderSideMenuEditForm'
import BuilderSideMenuEditPage from 'pages/FormBuilder/Components/BuilderSideMenuEditPage'
import BuilderSideMenuEditQuestion from 'pages/FormBuilder/Components/BuilderSideMenuEditQuestion'
import QuestionTypesList from 'pages/FormBuilder/Components/QuestionTypesList'
import * as React from 'react'
import { useHistory } from 'react-router-dom'
import type { CollapseItemProps } from 'ui/atoms/Collapse'
import Collapse from 'ui/atoms/Collapse'
import Space from 'ui/atoms/Space'
import { SELECTION_TYPE } from 'utils/FormBuilder/ConstantsFormBuilder'
import useBuilderQuestion from 'utils/FormBuilder/hooks/useBuilderQuestion'
import useBuilderQuestionOptions from 'utils/FormBuilder/hooks/useBuilderQuestionOptions'

import useIntegration from '@/hooks/useIntegration'
import BuilderSideMenuIntegration from '@/pages/FormBuilder/Components/Integration/BuilderSideMenuIntegration'

function FormBuilderSideMenu(): React.Node {
  const { selectedItem } = React.useContext(FormBuilderContext)
  const { integration, integrationTitledName } = useIntegration()
  const history = useHistory()

  const question = useBuilderQuestion(selectedItem?.id)

  const { hasRequiredAndOfficeUse, hasReadOnly } = useBuilderQuestionOptions(
    question?.question_type?.question_subtype,
    question?.question_type?.question_type
  )

  const renderSideMenuContent = React.useCallback(() => {
    if (selectedItem?.type === SELECTION_TYPE.PAGE) {
      return <BuilderSideMenuEditPage />
    }
    if (selectedItem?.type === SELECTION_TYPE.QUESTION) {
      return <BuilderSideMenuEditQuestion />
    }
    if (
      selectedItem?.type === SELECTION_TYPE.ROW_ADD ||
      selectedItem?.type === SELECTION_TYPE.QUESTION_ADD
    ) {
      return <QuestionTypesList />
    }
    return <BuilderSideMenuEditForm />
  }, [selectedItem?.type])

  const collapseItems = React.useMemo(() => {
    const items: CollapseItemProps[] = []
    if (
      hasReadOnly ||
      hasRequiredAndOfficeUse ||
      question?.question_type?.question_type === 'datetime'
    ) {
      items.push({
        key: 'advanced_settings',
        label: (
          <span className="text-base font-semibold text-gray-700">
            Advanced Settings
          </span>
        ),
        children: <BuilderSideMenuAdvancedSettings />,
        style: {
          borderBottom: 0,
          borderTop: '1px solid #d9d9d9',
        },
      })
    }
    if (
      !['handout'].includes(question?.question_type?.question_subtype) &&
      !!integration
    ) {
      items.push({
        key: integration,
        label: (
          <span className="text-base font-semibold text-gray-700">
            {integrationTitledName} Settings
          </span>
        ),
        children: <BuilderSideMenuIntegration key={question?.id} />,
        style: {
          borderBottom: 0,
          borderTop: '1px solid #d9d9d9',
        },
      })
    }
    return items
  }, [
    hasReadOnly,
    hasRequiredAndOfficeUse,
    integration,
    question?.id,
    question?.question_type?.question_subtype,
    question?.question_type?.question_type,
    integrationTitledName,
  ])

  const isSettingsActive = history.location.pathname.includes('settings')
  const isDesignActive = history.location.pathname.includes('design')

  return (
    <div>
      {isSettingsActive ? <BuilderSideMenuSettings /> : null}
      {!isSettingsActive && !isDesignActive ? (
        <Space size="large">
          {renderSideMenuContent()}

          {selectedItem?.type === SELECTION_TYPE.QUESTION && (
            <div
              style={{
                marginLeft: -24,
                marginRight: -24,
              }}
            >
              <Collapse
                bordered={false}
                className="bg-white"
                items={collapseItems}
              />
            </div>
          )}
        </Space>
      ) : null}
    </div>
  )
}

export default FormBuilderSideMenu
